import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonAccordion,
  IonAccordionGroup,
  IonButtons,
  IonIcon,
  IonModal,
  IonBackButton,
  IonSpinner,
  IonChip,
  IonBadge,
  IonGrid,
  IonRow,
  IonCol,
  IonSkeletonText,
  IonSegment,
  IonSegmentButton,
  IonNote,
  IonAvatar,
  IonSearchbar,
} from "@ionic/react";
import {
  create,
  trash,
  close,
  arrowBack,
  alertCircleOutline,
  help,
  megaphone,
  refresh,
  send,
  add,
  remove,
  chevronForward,
  chevronBack,
  time,
  person,
} from "ionicons/icons";
import { baseUrl, config, requestID } from "../../../common/utils";
import axios from "axios";

interface FaqItem {
  faq_id: number;
  question: string;
  answer: string;
}

interface NewsItem {
  news_id: number;
  title: string;
  body: string;
  author?: string;
  created_at?: string;
}

interface NotificationItem {
  notification_id: number;
  message: string;
  recipients?: string;
  created_at?: string;
  status?: string;
}

const ContentCommunication: React.FC = () => {
  // Modern color palette with improved contrast
  const colors = {
    primary: "#6366f1",
    primaryLight: "#818cf8",
    primaryDark: "#4f46e5",
    background: "#f8fafc",
    surface: "#ffffff",
    textPrimary: "#1e293b",
    textSecondary: "#475569",
    textTertiary: "#94a3b8",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    gradientLight: "linear-gradient(135deg, #6366f115 0%, #8b5cf615 100%)",
  };

  // Notification state
  const [notification, setNotification] = useState("");
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);

  // FAQ state
  const [faqQuestion, setFaqQuestion] = useState("");
  const [faqAnswer, setFaqAnswer] = useState("");
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  const [addingFaq, setAddingFaq] = useState(false);
  const [editingFaqSubmitting, setEditingFaqSubmitting] = useState(false);
  const [deletingFaqId, setDeletingFaqId] = useState<number | null>(null);

  // FAQ Pagination
  const [faqCurrentPage, setFaqCurrentPage] = useState(1);
  const [faqTotalPages, setFaqTotalPages] = useState(1);
  const [faqSearchTerm, setFaqSearchTerm] = useState("");

  // News state
  const [newsTitle, setNewsTitle] = useState("");
  const [newsContent, setNewsContent] = useState("");
  const [news, setNews] = useState<NewsItem[]>([]);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [loadingNews, setLoadingNews] = useState(false);
  const [addingNews, setAddingNews] = useState(false);
  const [editingNewsSubmitting, setEditingNewsSubmitting] = useState(false);
  const [deletingNewsId, setDeletingNewsId] = useState<number | null>(null);

  // News Pagination
  const [newsCurrentPage, setNewsCurrentPage] = useState(1);
  const [newsTotalPages, setNewsTotalPages] = useState(1);
  const [newsSearchTerm, setNewsSearchTerm] = useState("");

  // Notification state
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Active section
  const [activeSection, setActiveSection] = useState<
    "notifications" | "faq" | "news"
  >("notifications");

  const ITEMS_PER_PAGE = 10;

  /* ----------------------- Utilities ----------------------- */
  const safeRequestID = () => {
    try {
      return requestID();
    } catch (e) {
      console.warn("requestID() failed", e);
      return "";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const addRecipient = () => {
    if (newRecipient.trim() && !recipients.includes(newRecipient.trim())) {
      setRecipients([...recipients, newRecipient.trim()]);
      setNewRecipient("");
    }
  };

  const removeRecipient = (index: number) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  /* ----------------------- API Functions ----------------------- */
  const sendNotification = async () => {
    if (!notification || notification.trim().length === 0) return;
    setSendingNotification(true);

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("message", notification);
      formData.append("recipients", recipients.join(","));

      const response = await axios.post(
        baseUrl() + "Beneficiarys/sendNotification",
        formData,
        config
      );

      setNotification("");
      setRecipients([]);
      getPushNotification();
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      setSendingNotification(false);
    }
  };

  const getPushNotification = async () => {
    setLoadingNotifications(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Beneficiarys/getPushNotification",
        formData,
        config
      );

      const data = response.data?.data ?? [];
      const notificationData = Array.isArray(data) ? data : [];
      setNotifications(notificationData);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const getAdminFaqs = async (page: number = 1) => {
    setLoadingFaqs(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Faqsandterms/getAdminFaqs",
        formData,
        config
      );

      const data = response.data?.data ?? [];
      const allFaqs = Array.isArray(data) ? data : [];

      const filteredFaqs = faqSearchTerm
        ? allFaqs.filter(
            (faq) =>
              faq.question
                .toLowerCase()
                .includes(faqSearchTerm.toLowerCase()) ||
              faq.answer.toLowerCase().includes(faqSearchTerm.toLowerCase())
          )
        : allFaqs;

      const totalPages = Math.ceil(filteredFaqs.length / ITEMS_PER_PAGE);
      setFaqTotalPages(totalPages);

      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedFaqs = filteredFaqs.slice(startIndex, endIndex);

      setFaqs(paginatedFaqs);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
    } finally {
      setLoadingFaqs(false);
    }
  };

  const addFaq = async () => {
    if (!faqQuestion || !faqAnswer) return;
    setAddingFaq(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("question", faqQuestion);
      formData.append("answer", faqAnswer);

      await axios.post(baseUrl() + "Faqsandterms/addfaq", formData, config);

      await getAdminFaqs(faqCurrentPage);
      setFaqQuestion("");
      setFaqAnswer("");
    } catch (error) {
      console.error("Error adding FAQ:", error);
    } finally {
      setAddingFaq(false);
    }
  };

  const editFaqApi = async () => {
    if (!editingFaq) return;
    setEditingFaqSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("faq_id", String(editingFaq.faq_id));
      formData.append("id", String(editingFaq.faq_id));
      formData.append("question", editingFaq.question);
      formData.append("answer", editingFaq.answer);

      await axios.post(baseUrl() + "Faqsandterms/editfaq", formData, config);

      await getAdminFaqs(faqCurrentPage);
      setEditingFaq(null);
    } catch (error) {
      console.error("Error editing FAQ:", error);
    } finally {
      setEditingFaqSubmitting(false);
    }
  };

  const deleteFaq = async (faq_id: number) => {
    setDeletingFaqId(faq_id);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("faq_id", String(faq_id));

      await axios.post(baseUrl() + "Faqsandterms/deletefaq", formData, config);

      await getAdminFaqs(faqCurrentPage);
    } catch (error) {
      console.error("Error deleting FAQ:", error);
    } finally {
      setDeletingFaqId(null);
    }
  };

  const getNews = async (page: number = 1) => {
    setLoadingNews(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "Beneficiarys/getNews",
        formData,
        config
      );

      const data = response.data?.data ?? [];
      const allNews = Array.isArray(data) ? data : [];

      const filteredNews = newsSearchTerm
        ? allNews.filter(
            (newsItem) =>
              newsItem.title
                .toLowerCase()
                .includes(newsSearchTerm.toLowerCase()) ||
              newsItem.body.toLowerCase().includes(newsSearchTerm.toLowerCase())
          )
        : allNews;

      const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
      setNewsTotalPages(totalPages);

      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedNews = filteredNews.slice(startIndex, endIndex);

      setNews(paginatedNews);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoadingNews(false);
    }
  };

  const addNews = async () => {
    if (!newsTitle || !newsContent) return;
    setAddingNews(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("title", newsTitle);
      formData.append("body", newsContent);

      await axios.post(baseUrl() + "Beneficiarys/addNews", formData, config);

      await getNews(newsCurrentPage);
      setNewsTitle("");
      setNewsContent("");
    } catch (error) {
      console.error("Error adding News:", error);
    } finally {
      setAddingNews(false);
    }
  };

  const editNewsApi = async () => {
    if (!editingNews) return;
    setEditingNewsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("news_id", String(editingNews.news_id));
      formData.append("title", String(editingNews.title));
      formData.append("body", String(editingNews.body));
      if (editingNews.author) formData.append("author", editingNews.author);

      await axios.post(baseUrl() + "Beneficiarys/editnews", formData, config);

      await getNews(newsCurrentPage);
      setEditingNews(null);
    } catch (error) {
      console.error("Error editing News:", error);
    } finally {
      setEditingNewsSubmitting(false);
    }
  };

  const deleteNewsApi = async (news_id: number) => {
    setDeletingNewsId(news_id);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("news_id", String(news_id));

      await axios.post(baseUrl() + "Beneficiarys/deletenews", formData, config);

      await getNews(newsCurrentPage);
    } catch (error) {
      console.error("Error deleting News:", error);
    } finally {
      setDeletingNewsId(null);
    }
  };

  /* ----------------------- Effects ----------------------- */
  useEffect(() => {
    getAdminFaqs();
    getNews();
    getPushNotification();
  }, []);

  useEffect(() => {
    getAdminFaqs(1);
  }, [faqSearchTerm]);

  useEffect(() => {
    getNews(1);
  }, [newsSearchTerm]);

  /* ----------------------- UI Components ----------------------- */
  const PaginationControls = ({
    currentPage,
    totalPages,
    onPageChange,
  }: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  }) => (
    <div className="pagination-controls">
      <IonButton
        fill="clear"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-btn"
      >
        <IonIcon icon={chevronBack} slot="start" />
        Previous
      </IonButton>

      <IonChip className="page-info-chip">
        <IonLabel>
          Page {currentPage} of {totalPages}
        </IonLabel>
      </IonChip>

      <IonButton
        fill="clear"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-btn"
      >
        Next
        <IonIcon icon={chevronForward} slot="end" />
      </IonButton>
    </div>
  );

  const SectionHeader = ({
    icon,
    title,
    count,
  }: {
    icon: string;
    title: string;
    count?: number;
  }) => (
    <div className="section-header">
      <div className="section-title">
        <IonIcon icon={icon} className="section-icon" />
        <span>{title}</span>
        {count !== undefined && (
          <IonBadge color="primary" className="count-badge">
            {count}
          </IonBadge>
        )}
      </div>
    </div>
  );

  const LoadingSkeleton = ({
    type,
  }: {
    type: "faq" | "news" | "notification";
  }) => (
    <div className="skeleton-container">
      {[...Array(3)].map((_, index) => (
        <div key={index} className={`skeleton-item ${type}`}>
          {type === "faq" && (
            <>
              <div className="skeleton-line wide" />
              <div className="skeleton-line medium" />
              <div className="skeleton-line full" />
              <div className="skeleton-line full" />
            </>
          )}
          {type === "news" && (
            <>
              <div className="skeleton-avatar" />
              <div className="skeleton-content">
                <div className="skeleton-line wide" />
                <div className="skeleton-line full" />
                <div className="skeleton-line medium" />
              </div>
            </>
          )}
          {type === "notification" && (
            <>
              <div className="skeleton-avatar" />
              <div className="skeleton-content">
                <div className="skeleton-line wide" />
                <div className="skeleton-line full" />
                <div className="skeleton-line medium" />
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );

  const EmptyState = ({
    icon,
    title,
    description,
  }: {
    icon: string;
    title: string;
    description: string;
  }) => (
    <div className="empty-state">
      <IonIcon icon={icon} className="empty-icon" />
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );

  /* ----------------------- Render ----------------------- */
  return (
    <IonPage>
      <IonHeader className="modern-header">
        <IonToolbar className="primary-toolbar">
          <IonButtons slot="start">
            <IonBackButton icon={arrowBack} className="back-button" />
          </IonButtons>
          <IonTitle>Content & Communication</IonTitle>
        </IonToolbar>

        <IonToolbar className="segment-toolbar">
          <IonSegment
            value={activeSection}
            onIonChange={(e) => setActiveSection(e.detail.value as any)}
            className="modern-segment"
          >
            <IonSegmentButton value="notifications" className="segment-btn">
              <IonIcon icon={alertCircleOutline} />
              <IonLabel>Notifications</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="faq" className="segment-btn">
              <IonIcon icon={help} />
              <IonLabel>FAQ</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="news" className="segment-btn">
              <IonIcon icon={megaphone} />
              <IonLabel>News</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      <IonContent className="modern-content">
        {/* Notifications Section */}
        {activeSection === "notifications" && (
          <div className="section-container">
            {/* Send Notification Card */}
            <IonCard className="modern-card">
              <IonCardHeader className="card-header-gradient">
                <SectionHeader
                  icon={alertCircleOutline}
                  title="Send Push Notifications"
                />
              </IonCardHeader>
              <IonCardContent>
                <div className="form-group">
                  <IonTextarea
                    placeholder="Enter notification message..."
                    value={notification}
                    onIonChange={(e) => setNotification(e.detail.value!)}
                    rows={4}
                    className="modern-textarea"
                  />
                </div>

                <div className="form-group">
                  <IonLabel className="input-label">
                    Recipients (Phone Numbers)
                  </IonLabel>
                  <div className="recipient-input-group">
                    <IonInput
                      placeholder="Enter phone number"
                      value={newRecipient}
                      onIonChange={(e) => setNewRecipient(e.detail.value!)}
                      onKeyPress={(e) => e.key === "Enter" && addRecipient()}
                      className="modern-input"
                    />
                    <IonButton
                      onClick={addRecipient}
                      disabled={!newRecipient.trim()}
                      className="add-recipient-btn"
                    >
                      <IonIcon icon={add} />
                    </IonButton>
                  </div>

                  <div className="recipients-chips">
                    {recipients.map((recipient, index) => (
                      <IonChip key={index} className="recipient-chip">
                        <IonLabel>{recipient}</IonLabel>
                        <IonIcon
                          icon={remove}
                          onClick={() => removeRecipient(index)}
                          className="remove-chip-icon"
                        />
                      </IonChip>
                    ))}
                    {recipients.length === 0 && (
                      <IonNote className="helper-text">
                        No recipients added. Notification will be sent to all
                        users.
                      </IonNote>
                    )}
                  </div>
                </div>

                <IonButton
                  expand="block"
                  onClick={sendNotification}
                  disabled={sendingNotification || !notification.trim()}
                  className="primary-action-btn"
                >
                  {sendingNotification ? (
                    <>
                      <IonSpinner name="crescent" className="btn-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <IonIcon icon={send} className="btn-icon" />
                      Send Notification
                    </>
                  )}
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Notification History */}
            <IonCard className="modern-card">
              <IonCardHeader className="card-header-gradient">
                <SectionHeader
                  icon={megaphone}
                  title="Notification History"
                  count={notifications.length}
                />
              </IonCardHeader>
              <IonCardContent>
                <div className="section-actions">
                  <IonButton
                    onClick={getPushNotification}
                    disabled={loadingNotifications}
                    className="refresh-btn"
                  >
                    <IonIcon icon={refresh} slot="start" />
                    Refresh
                  </IonButton>
                </div>

                {loadingNotifications ? (
                  <LoadingSkeleton type="notification" />
                ) : notifications.length === 0 ? (
                  <EmptyState
                    icon={megaphone}
                    title="No notifications sent yet"
                    description="Notifications you send will appear here"
                  />
                ) : (
                  <IonList className="modern-list">
                    {notifications.map((item) => (
                      <IonItem key={item.notification_id} className="list-item">
                        <IonAvatar slot="start" className="notification-avatar">
                          <IonIcon icon={megaphone} />
                        </IonAvatar>
                        <IonLabel className="item-content">
                          <h3 className="item-title">{item.message}</h3>
                          {item.recipients && (
                            <p className="item-subtitle">
                              <IonIcon icon={person} className="inline-icon" />
                              To: {item.recipients}
                            </p>
                          )}
                          {item.created_at && (
                            <IonNote className="item-meta">
                              <IonIcon icon={time} className="inline-icon" />
                              {formatDate(item.created_at)}
                            </IonNote>
                          )}
                        </IonLabel>
                        {item.status && (
                          <IonBadge
                            slot="end"
                            className={`status-badge ${item.status}`}
                          >
                            {item.status}
                          </IonBadge>
                        )}
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </IonCardContent>
            </IonCard>
          </div>
        )}

        {/* FAQ Section */}
        {activeSection === "faq" && (
          <IonCard className="modern-card">
            <IonCardHeader className="card-header-gradient">
              <SectionHeader
                icon={help}
                title="FAQ / Helpdesk"
                count={faqs.length}
              />
            </IonCardHeader>
            <IonCardContent>
              {/* Add FAQ Form */}
              <div className="add-form">
                <IonInput
                  placeholder="Enter FAQ question"
                  value={faqQuestion}
                  onIonChange={(e) => setFaqQuestion(e.detail.value!)}
                  className="modern-input"
                />
                <IonTextarea
                  placeholder="Enter FAQ answer"
                  value={faqAnswer}
                  onIonChange={(e) => setFaqAnswer(e.detail.value!)}
                  autoGrow
                  rows={3}
                  className="modern-textarea"
                />
                <IonButton
                  expand="block"
                  onClick={addFaq}
                  disabled={
                    addingFaq || !faqQuestion.trim() || !faqAnswer.trim()
                  }
                  className="primary-action-btn"
                >
                  {addingFaq ? (
                    <>
                      <IonSpinner name="crescent" className="btn-spinner" />
                      Adding...
                    </>
                  ) : (
                    "Add FAQ"
                  )}
                </IonButton>
              </div>

              {/* FAQ List */}
              <div className="content-section">
                <div className="section-actions">
                  <IonSearchbar
                    value={faqSearchTerm}
                    onIonInput={(e) => setFaqSearchTerm(e.detail.value!)}
                    placeholder="Search FAQs..."
                    className="modern-searchbar"
                  />
                </div>

                {loadingFaqs && faqs.length === 0 ? (
                  <LoadingSkeleton type="faq" />
                ) : faqs.length === 0 ? (
                  <EmptyState
                    icon={help}
                    title="No FAQs yet"
                    description="Add your first FAQ to get started"
                  />
                ) : (
                  <>
                    <IonAccordionGroup className="modern-accordion">
                      {faqs.map((faq) => (
                        <IonAccordion
                          key={faq.faq_id}
                          value={`faq-${faq.faq_id}`}
                          className="faq-accordion"
                        >
                          <IonItem slot="header" className="accordion-header">
                            <IonLabel className="faq-question">
                              <h3>{faq.question}</h3>
                              <p>Click to view answer</p>
                            </IonLabel>
                            <IonButtons slot="end" className="action-buttons">
                              <IonButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingFaq(faq);
                                }}
                                className="edit-btn"
                                fill="solid"
                              >
                                <IonIcon icon={create} />
                              </IonButton>
                              <IonButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFaq(faq.faq_id);
                                }}
                                disabled={deletingFaqId === faq.faq_id}
                                className="delete-btn"
                                fill="solid"
                              >
                                {deletingFaqId === faq.faq_id ? (
                                  <IonSpinner name="crescent" />
                                ) : (
                                  <IonIcon icon={trash} />
                                )}
                              </IonButton>
                            </IonButtons>
                          </IonItem>
                          <div slot="content" className="accordion-content">
                            {faq.answer}
                          </div>
                        </IonAccordion>
                      ))}
                    </IonAccordionGroup>

                    {faqTotalPages > 1 && (
                      <PaginationControls
                        currentPage={faqCurrentPage}
                        totalPages={faqTotalPages}
                        onPageChange={(page) => {
                          setFaqCurrentPage(page);
                          getAdminFaqs(page);
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* News Section */}
        {activeSection === "news" && (
          <IonCard className="modern-card">
            <IonCardHeader className="card-header-gradient">
              <SectionHeader
                icon={megaphone}
                title="News & Announcements"
                count={news.length}
              />
            </IonCardHeader>
            <IonCardContent>
              {/* Add News Form */}
              <div className="add-form">
                <IonInput
                  placeholder="Enter news title"
                  value={newsTitle}
                  onIonChange={(e) => setNewsTitle(e.detail.value!)}
                  className="modern-input"
                />
                <IonTextarea
                  placeholder="Enter news content"
                  value={newsContent}
                  onIonChange={(e) => setNewsContent(e.detail.value!)}
                  autoGrow
                  rows={4}
                  className="modern-textarea"
                />
                <IonButton
                  expand="block"
                  onClick={addNews}
                  disabled={
                    addingNews || !newsTitle.trim() || !newsContent.trim()
                  }
                  className="primary-action-btn"
                >
                  {addingNews ? (
                    <>
                      <IonSpinner name="crescent" className="btn-spinner" />
                      Adding...
                    </>
                  ) : (
                    "Add News"
                  )}
                </IonButton>
              </div>

              {/* News List */}
              <div className="content-section">
                <div className="section-actions">
                  <IonSearchbar
                    value={newsSearchTerm}
                    onIonInput={(e) => setNewsSearchTerm(e.detail.value!)}
                    placeholder="Search news..."
                    className="modern-searchbar"
                  />
                </div>

                {loadingNews && news.length === 0 ? (
                  <LoadingSkeleton type="news" />
                ) : news.length === 0 ? (
                  <EmptyState
                    icon={megaphone}
                    title="No news yet"
                    description="Add your first news article to get started"
                  />
                ) : (
                  <>
                    <IonList className="modern-list">
                      {news.map((item) => (
                        <IonItem key={item.news_id} className="list-item">
                          <IonAvatar slot="start" className="news-avatar">
                            <IonIcon icon={megaphone} />
                          </IonAvatar>
                          <IonLabel className="item-content">
                            <h3 className="item-title">{item.title}</h3>
                            <p className="item-description">
                              {item.body.length > 100
                                ? `${item.body.substring(0, 100)}...`
                                : item.body}
                            </p>
                            {item.created_at && (
                              <IonNote className="item-meta">
                                <IonIcon icon={time} className="inline-icon" />
                                {formatDate(item.created_at)}
                              </IonNote>
                            )}
                          </IonLabel>
                          <IonButtons slot="end" className="action-buttons">
                            <IonButton
                              onClick={() => setEditingNews(item)}
                              className="edit-btn"
                              fill="solid"
                            >
                              <IonIcon icon={create} />
                            </IonButton>
                            <IonButton
                              onClick={() => deleteNewsApi(item.news_id)}
                              disabled={deletingNewsId === item.news_id}
                              className="delete-btn"
                              fill="solid"
                            >
                              {deletingNewsId === item.news_id ? (
                                <IonSpinner name="crescent" />
                              ) : (
                                <IonIcon icon={trash} />
                              )}
                            </IonButton>
                          </IonButtons>
                        </IonItem>
                      ))}
                    </IonList>

                    {newsTotalPages > 1 && (
                      <PaginationControls
                        currentPage={newsCurrentPage}
                        totalPages={newsTotalPages}
                        onPageChange={(page) => {
                          setNewsCurrentPage(page);
                          getNews(page);
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* Edit Modals */}
        <IonModal
          isOpen={!!editingFaq}
          onDidDismiss={() => setEditingFaq(null)}
          className="modern-modal"
        >
          <IonHeader>
            <IonToolbar className="modal-toolbar">
              <IonTitle>Edit FAQ</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => setEditingFaq(null)}
                  className="close-btn"
                >
                  <IonIcon icon={close} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="modal-content">
            <IonInput
              placeholder="Edit question"
              value={editingFaq?.question ?? ""}
              onIonChange={(e) =>
                setEditingFaq((prev) =>
                  prev ? { ...prev, question: e.detail.value! } : prev
                )
              }
              className="modern-input"
            />
            <IonTextarea
              placeholder="Edit answer"
              value={editingFaq?.answer ?? ""}
              onIonChange={(e) =>
                setEditingFaq((prev) =>
                  prev ? { ...prev, answer: e.detail.value! } : prev
                )
              }
              autoGrow
              rows={4}
              className="modern-textarea"
            />
            <IonButton
              expand="block"
              onClick={editFaqApi}
              disabled={editingFaqSubmitting}
              className="primary-action-btn"
            >
              {editingFaqSubmitting ? (
                <>
                  <IonSpinner name="crescent" className="btn-spinner" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </IonButton>
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={!!editingNews}
          onDidDismiss={() => setEditingNews(null)}
          className="modern-modal"
        >
          <IonHeader>
            <IonToolbar className="modal-toolbar">
              <IonTitle>Edit News</IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => setEditingNews(null)}
                  className="close-btn"
                >
                  <IonIcon icon={close} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="modal-content">
            <IonInput
              placeholder="Edit news title"
              value={editingNews?.title ?? ""}
              onIonChange={(e) =>
                setEditingNews((prev) =>
                  prev ? { ...prev, title: e.detail.value! } : prev
                )
              }
              className="modern-input"
            />
            <IonTextarea
              placeholder="Edit news content"
              value={editingNews?.body ?? ""}
              onIonChange={(e) =>
                setEditingNews((prev) =>
                  prev ? { ...prev, body: e.detail.value! } : prev
                )
              }
              autoGrow
              rows={6}
              className="modern-textarea"
            />
            <IonButton
              expand="block"
              onClick={editNewsApi}
              disabled={editingNewsSubmitting}
              className="primary-action-btn"
            >
              {editingNewsSubmitting ? (
                <>
                  <IonSpinner name="crescent" className="btn-spinner" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default ContentCommunication;

// Add this CSS to your global styles or component-specific styles
const modernStyles = `
/* Modern Layout Styles */
.modern-header {
  --background: transparent;
}

.primary-toolbar {
  --background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  --color: white;
  --border-width: 0;
}

.back-button {
  --color: white;
}

.segment-toolbar {
  --background: #ffffff;
  --min-height: 80px;
  --border-width: 0;
  padding: 8px 16px;
}

.modern-segment {
  --background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.segment-btn {
  --background-checked: #6366f1;
  --color: #64748b;
  --color-checked: white;
  --indicator-color: transparent;
  border-radius: 8px;
  min-height: 40px;
  --padding-start: 12px;
  --padding-end: 12px;
}

.modern-content {
  --background: #f8fafc;
}

.section-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
}

.modern-card {
  --background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  margin: 16px 0;
  overflow: hidden;
}

.card-header-gradient {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 16px 16px 0 0;
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 600;
}

.section-icon {
  font-size: 1.2rem;
}

.count-badge {
  margin-left: 8px;
}

/* Form Styles */
.form-group {
  margin-bottom: 20px;
}

.input-label {
  color: #1e293b;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
}

.recipient-input-group {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.modern-input, .modern-textarea {
  --background: #ffffff;
  --color: #1e293b;
  --placeholder-color: #94a3b8;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  --padding-start: 16px;
  --padding-end: 16px;
}

.modern-textarea {
  min-height: 100px;
}

.add-recipient-btn {
  --background: #6366f1;
  --border-radius: 8px;
  min-width: 44px;
}

.recipients-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.recipient-chip {
  background: linear-gradient(135deg, #6366f115 0%, #8b5cf615 100%);
  color: #6366f1;
  border: 1px solid #6366f120;
}

.remove-chip-icon {
  cursor: pointer;
  font-size: 14px;
}

.helper-text {
  color: #94a3b8;
  font-size: 0.875rem;
}

/* Button Styles */
.primary-action-btn {
  --background: #6366f1;
  --background-hover: #4f46e5;
  --background-activated: #4f46e5;
  --border-radius: 12px;
  font-weight: 500;
  height: 44px;
  --box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.btn-spinner {
  margin-right: 8px;
}

.btn-icon {
  margin-right: 8px;
}

.refresh-btn {
  --background: #6366f1;
  --border-radius: 8px;
}

/* List & Accordion Styles */
.section-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.modern-searchbar {
  --background: #ffffff;
  --border-radius: 12px;
  --box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.modern-list {
  background: transparent;
}

.list-item {
  --background: #ffffff;
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  --padding-start: 16px;
  --padding-end: 16px;
  --inner-padding-end: 8px;
}

.notification-avatar, .news-avatar {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-avatar ion-icon, .news-avatar ion-icon {
  color: white;
  font-size: 18px;
}

.item-content {
  margin: 8px 0;
}

.item-title {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  font-size: 0.95rem;
  line-height: 1.4;
}

.item-subtitle, .item-description {
  color: #475569;
  margin-bottom: 4px;
  font-size: 0.875rem;
  line-height: 1.4;
}

.item-meta {
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}

.inline-icon {
  font-size: 12px;
}

.status-badge {
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 12px;
}

.status-badge.sent {
  background: #10b981;
}

.status-badge.pending {
  background: #f59e0b;
}

/* Accordion Styles */
.modern-accordion {
  margin-top: 16px;
}

.faq-accordion {
  margin-bottom: 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.accordion-header {
  --background: #ffffff;
  border-radius: 12px;
  --inner-padding-end: 8px;
}

.faq-question h3 {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.faq-question p {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 4px;
}

.accordion-content {
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
  color: #475569;
  line-height: 1.5;
  padding: 16px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 4px;
}

.edit-btn {
  --color: #6366f1;
  --background: #6366f110;
  --border-radius: 8px;
  --padding-start: 8px;
  --padding-end: 8px;
  min-width: 36px;
  min-height: 36px;
}

.delete-btn {
  --color: #ef4444;
  --background: #ef444410;
  --border-radius: 8px;
  --padding-start: 8px;
  --padding-end: 8px;
  min-width: 36px;
  min-height: 36px;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.pagination-btn {
  --color: #6366f1;
  --color-disabled: #94a3b8;
}

.page-info-chip {
  background: linear-gradient(135deg, #6366f115 0%, #8b5cf615 100%);
  color: #6366f1;
  border: 1px solid #6366f120;
}

/* Loading Skeletons */
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.skeleton-item.faq {
  flex-direction: column;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-line {
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  animation: pulse 2s infinite;
}

.skeleton-line.wide { width: 80%; }
.skeleton-line.medium { width: 60%; }
.skeleton-line.full { width: 100%; }

.skeleton-avatar {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  color: #475569;
  margin-bottom: 8px;
  font-size: 1.1rem;
}

.empty-state p {
  font-size: 0.9rem;
}

/* Modal Styles */
.modern-modal {
  --border-radius: 20px;
  --box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-toolbar {
  --background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  --color: white;
}

.close-btn {
  --color: white;
}

.modal-content {
  --background: #f8fafc;
  --padding-start: 20px;
  --padding-end: 20px;
  --padding-top: 20px;
  --padding-bottom: 20px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .section-container {
    padding: 0 12px;
  }
  
  .modern-segment {
    max-width: 100%;
  }
  
  .segment-btn ion-label {
    font-size: 0.8rem;
  }
  
  .recipient-input-group {
    flex-direction: column;
  }
  
  .add-recipient-btn {
    width: 100%;
  }
  
  .section-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .modern-searchbar {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .card-header-gradient {
    padding: 16px;
  }
  
  .section-title {
    font-size: 1rem;
  }
}
`;

// Add the styles to the document
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = modernStyles;
  document.head.appendChild(styleElement);
}
